





























import Vue from "vue";
import axios from "axios";
import config from "@/config/constants";
import { isAffirmative } from "@/utils/isAffirmative/isAffirmative";

// The validate-email endpoint returns { message, success } (not the shared
// {success, data}/{success, err} envelope) and has no machine-readable error
// code, so outcomes are classified by matching the human-readable message.
type VerificationOutcome =
  | "loading"
  | "verified"
  | "alreadyVerified"
  | "expired"
  | "notRecognised"
  | "invalid"
  | "error";

type OutcomeView = {
  title: string;
  message: string;
  // A CTA routing to /login when the user can act (log in, then request a
  // fresh link via the in-app modal); null renders a plain Close button.
  ctaLabel?: string;
  ctaTo?: string;
};

const OUTCOME_VIEWS: Record<Exclude<VerificationOutcome, "loading">, OutcomeView> = {
  verified: {
    title: "Your email has been verified",
    message: "Thanks for confirming your email address."
  },
  alreadyVerified: {
    title: "This email is already verified",
    message: "Your email address has already been confirmed, so there's nothing more to do."
  },
  expired: {
    title: "This verification link has expired",
    message: "For your security, verification links expire after a while. Please request a new verification email from the app/website.",
  },
  notRecognised: {
    title: "This verification link wasn't recognised",
    message: "This link may have already been used or is no longer valid. Please request a new verification email from the app/website.",
  },
  invalid: {
    title: "This link is invalid",
    message: "We couldn't read a valid verification link. Please open the most recent link from your email, or request a new one from the app/website.",
  },
  error: {
    title: "Something went wrong",
    message: "We couldn't verify your email just now. Please try again in a few minutes, or contact support if the problem continues.",
  },
};

export default Vue.extend({
  name: "VerifyEmail",
  data() {
    return {
      outcome: "loading" as VerificationOutcome,
    };
  },
  computed: {
    clientLogo(): string {
      const hardcoded = this.$store.getters.hardcoded;
      if (hardcoded && hardcoded.logo) {
        return hardcoded.logo;
      }
      return this.$store.getters.client.client_logo;
    },
    view(): OutcomeView {
      return OUTCOME_VIEWS[this.outcome as Exclude<VerificationOutcome, "loading">];
    },
  },
  mounted(): void {
    const token = this.$route.query.token;
    if (typeof token !== "string" || token.length === 0) {
      this.outcome = "invalid";
      return;
    }
    this.validateEmail(token);
  },
  methods: {
    dismiss() {
      this.$router.push("/");
    },
    async validateEmail(token: string): Promise<void> {
      try {
        const response = await axios.post(
          `${config.api_env}/application/api/u/auth/validate-email`,
          { token },
          config.options
        );
        this.outcome = this.classifyOutcome(response.data);
      } catch {
        this.outcome = "error";
      }
    },
    classifyOutcome(data: { message?: string; success?: unknown }): VerificationOutcome {
      const message = data.message ?? "";
      if (isAffirmative(data.success)) {
        // "already verified" also returns success:true — surface it distinctly
        // so the copy points straight to login rather than "just verified".
        return /already/i.test(message) ? "alreadyVerified" : "verified";
      }
      if (/expired/i.test(message)) {
        return "expired";
      }
      if (/not set/i.test(message)) {
        return "invalid";
      }
      return "notRecognised";
    },
  },
});
